import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

/**
 * 参数示例:
 * ```typescript
 * {
 *   "prompt": "A serene lake surrounded by mountains at sunset, with gentle waves and reflections",
 *   "durationSeconds": 5,
 *   "aspectRatio": "16:9",
 *   "enhancePrompt": true,
 *   "personGeneration": "allow_adult",
 *   "sampleCount": 1
 * }
 * ```
 */
export interface GoogleVeo20GenerateExpParams {
  /**
   * 视频生成提示词，描述要生成的视频内容，长度不超过2000字符
   */
  prompt: string;
  /**
   * 输入图像，用于图像到视频(I2V)模式，支持URL、base64数据（data:image/...格式）或包含bytesBase64Encoded/gcsUri的对象
   */
  image?: string | any;
  /**
   * 参考图像列表，用于引导视频生成风格或包含特定素材，最多3张素材或1张风格图像
   */
  referenceImages?: Array<{
    /**
     * 参考图像，支持URL、base64数据或包含bytesBase64Encoded/gcsUri的对象
     */
    image: string | any;
    /**
     * 参考类型，可选值：asset（素材，最多3张）或 style（风格，最多1张），默认asset
     * @default "asset"
     * @values asset | style
     */
    referenceType?: 'asset' | 'style';
  }>;
  /**
   * 负向提示词，描述不希望在视频中出现的内容
   */
  negativePrompt?: string;
  /**
   * 视频时长（秒），支持5、6、7或8秒，默认5秒
   * @default 5
   * @values 5 | 6 | 7 | 8
   */
  durationSeconds?: 5 | 6 | 7 | 8;
  /**
   * 视频宽高比，支持9:16（竖屏）或16:9（横屏），默认16:9
   * @default "16:9"
   * @values 9:16 | 16:9
   */
  aspectRatio?: '9:16' | '16:9';
  /**
   * 是否启用提示词增强，开启后会自动优化和扩展提示词，默认true
   * @default true
   */
  enhancePrompt?: boolean;
  /**
   * 人物生成设置，可选值：dont_allow（不允许生成人物）或 allow_adult（允许生成成年人），默认allow_adult
   * @default "allow_adult"
   * @values dont_allow | allow_adult
   */
  personGeneration?: 'dont_allow' | 'allow_adult';
  /**
   * 生成样本数量，控制一次生成多少个视频，范围1-4，默认1
   * @default 1
   * @range 1 - 4
   */
  sampleCount?: number;
  /**
   * 随机种子，用于生成可重复的结果，范围0-4294967295
   * @range 0 - 4294967295
   */
  seed?: number;
  /**
   * GCS存储URI，用于指定视频存储位置，格式如 gs://bucket-name/path/。如不指定则返回base64编码的视频
   */
  storageUri?: string;
}

export interface GoogleVeo20GenerateExpResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * google_veo_20_generate_exp
 *
 * Google Veo 2.0 Generate Exp 视频生成接口，支持文本到视频(T2V)、图像到视频(I2V)和参考图像生成。
 * 
 * Model: veo-2.0-generate-exp
 * Features:
 * - 文本到视频生成（T2V）
 * - 图像到视频生成（I2V）
 * - 支持参考图像（素材或风格）
 * - 可自定义视频时长（5-8秒）
 * - 支持多种宽高比
 * - 提示词增强功能
 *
 * @example
 * ```typescript
 * import { initSeacloud, googleVeo20GenerateExp } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await googleVeo20GenerateExp(
 *   {
 *     "prompt": "A serene lake surrounded by mountains at sunset, with gentle waves and reflections",
 *     "durationSeconds": 5,
 *     "aspectRatio": "16:9",
 *     "enhancePrompt": true,
 *     "personGeneration": "allow_adult",
 *     "sampleCount": 1
 *   }
 * );
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 GoogleVeo20GenerateExpParams 接口定义
 * @returns 生成的资源列表
 */
export async function googleVeo20GenerateExp(
  params: GoogleVeo20GenerateExpParams,
): Promise<GoogleVeo20GenerateExpResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'google_veo_20_generate_exp',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: GoogleVeo20GenerateExpResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
